import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import './Technologies.css';

import reactLogo from '../assets/tech-logos/react.svg';
import flutterLogo from '../assets/tech-logos/flutter.svg';
import html5Logo from '../assets/tech-logos/html5.svg';
import css3Logo from '../assets/tech-logos/css3.svg';
import jsLogo from '../assets/tech-logos/javascript.svg';
import appleLogo from '../assets/tech-logos/apple.svg';
import awsLogo from '../assets/tech-logos/aws.svg';
import gcpLogo from '../assets/tech-logos/googlecloud.svg';
import firebaseLogo from '../assets/tech-logos/firebase.svg';
import cloudflareLogo from '../assets/tech-logos/cloudflare.svg';
import linuxLogo from '../assets/tech-logos/linux.svg';
import facebookLogo from '../assets/tech-logos/facebook.svg';
import laravelLogo from '../assets/tech-logos/laravel.svg';
import phpLogo from '../assets/tech-logos/php.svg';
import vuejsLogo from '../assets/tech-logos/vuejs.svg';
import nodejsLogo from '../assets/tech-logos/nodejs.svg';
import swiftLogo from '../assets/tech-logos/swift.svg';
import kotlinLogo from '../assets/tech-logos/kotlin.svg';
import claudeLogo from '../assets/tech-logos/claude.svg';
import openaiLogo from '../assets/tech-logos/openai.svg';
import geminiLogo from '../assets/tech-logos/gemini.svg';

const techCategories = [
  {
    key: 'frontend',
    techs: [
      { key: 'react', logo: reactLogo },
      { key: 'flutter', logo: flutterLogo },
      { key: 'vuejs', logo: vuejsLogo },
      { key: 'html5', logo: html5Logo },
      { key: 'css3', logo: css3Logo },
      { key: 'javascript', logo: jsLogo },
      { key: 'swift', logo: swiftLogo },
      { key: 'kotlin', logo: kotlinLogo },
      { key: 'apple', logo: appleLogo },
    ]
  },
  {
    key: 'backend',
    techs: [
      { key: 'laravel', logo: laravelLogo },
      { key: 'php', logo: phpLogo },
      { key: 'nodejs', logo: nodejsLogo },
    ]
  },
  {
    key: 'cloud',
    techs: [
      { key: 'aws', logo: awsLogo },
      { key: 'googleCloud', logo: gcpLogo },
      { key: 'firebase', logo: firebaseLogo },
      { key: 'cloudflare', logo: cloudflareLogo },
      { key: 'linux', logo: linuxLogo },
      { key: 'facebook', logo: facebookLogo },
    ]
  },
  {
    key: 'ai',
    techs: [
      { key: 'claude', logo: claudeLogo },
      { key: 'openai', logo: openaiLogo },
      { key: 'gemini', logo: geminiLogo },
    ]
  }
];

const Technologies = () => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);

  return (
    <section id="technologies" className="section tech-section">
      <div className="container text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">{t('technologies.sectionTag')}</span>
          <h2 className="section-title">{t('technologies.title')}</h2>
          <p className="section-subtitle">{t('technologies.subtitle')}</p>
        </motion.div>

        {techCategories.map((category, catIdx) => (
          <div key={category.key} className="tech-category">
            <motion.h3
              className="tech-category-title"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: catIdx * 0.1 }}
            >
              {t(`technologies.categories.${category.key}`)}
            </motion.h3>
            <div className="tech-grid">
              {category.techs.map((tech, idx) => (
                <motion.div
                  key={tech.key}
                  className="tech-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                  whileHover={{ y: -8, scale: 1.03 }}
                  onClick={() => setSelected(tech)}
                >
                  <div className="tech-logo">
                    <img src={tech.logo} alt={t(`technologies.techs.${tech.key}.name`)} loading="lazy" />
                  </div>
                  <h4 className="tech-name">{t(`technologies.techs.${tech.key}.name`)}</h4>
                  <p className="tech-desc">{t(`technologies.techs.${tech.key}.desc`)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        ))}

        <motion.div
          className="tech-highlight"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="tech-highlight-inner">
            <div className="tech-highlight-icon">🚀</div>
            <div>
              <h4>{t('technologies.highlight.title')}</h4>
              <p>{t('technologies.highlight.desc')}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tech Detail Modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            className="tech-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <motion.div
              className="tech-modal"
              initial={{ scale: 0.8, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="tech-modal-close" onClick={() => setSelected(null)}>
                <FiX size={22} />
              </button>
              <div className="tech-modal-header">
                <div className="tech-modal-logo">
                  <img src={selected.logo} alt={t(`technologies.techs.${selected.key}.name`)} />
                </div>
                <h3>{t(`technologies.techs.${selected.key}.name`)}</h3>
                <p className="tech-modal-tagline">{t(`technologies.techs.${selected.key}.desc`)}</p>
              </div>
              <div className="tech-modal-body">
                <h4>{t('technologies.modal.whyTitle')}</h4>
                <p>{t(`technologies.techs.${selected.key}.why`)}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Technologies;
